import { useEffect, lazy, Suspense } from 'react';
import {
  Route,
  Navigate,
  Outlet,
  ScrollRestoration,
  useParams,
  useSearchParams,
  createRoutesFromElements,
} from 'react-router-dom';

const PatientTimelineRedirect = () => {
  const { patientId } = useParams();
  return <Navigate to={`/patients/${patientId}?view=timeline`} replace />;
};

// The old per-patient Rx list (SimplePrescriptionsPage) was retired in favour
// of the modern pharmacy list, which accepts a `patientId` filter. Keep the
// old URL working by redirecting into the filtered pharmacy view.
const PatientPrescriptionsRedirect = () => {
  const { patientId } = useParams();
  return (
    <Navigate to={`/pharmacy/prescriptions?patientId=${patientId}`} replace />
  );
};
import { MainLayout } from '@/layouts/MainLayout';
import { useThemeStore } from '@/stores/useThemeStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { UserRole, Permission } from '@/types/user';
import { useTheme, isEffectiveDarkScheme } from '@/hooks/useTheme';
import { useAccessibilityStore } from '@/stores/useAccessibilityStore';
// Complex lab orders temporarily disabled - using simplified clinic lab orders instead
// import { useLabOrderStore } from '@/stores/useLabOrderStore';
import { useLocalizationStore } from '@/stores/useLocalizationStore';
import { useClinicalDecisionSupportStore } from '@/stores/useClinicalDecisionSupportStore';
import { useCPOEStore } from '@/stores/useCPOEStore';
import { useADTStore } from '@/stores/useADTStore';
import { AIAssistantPanel } from '@/components/AIAssistantPanel';
import { FeatureGuard } from '@/components/FeatureGuard';
import { ShellRoute } from '@/components/ShellRoute';
import { ROLE_ROUTES, doorRouteFor } from '@/pages/roles/registry';
import { RoleRouteGuard } from '@/pages/roles/RoleRouteGuard';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { DESIGN_ROLES } from '@/config/designRoles';
import { useMaieShell } from '@/config/maieShell';
import { NavDietRoute } from '@/components/NavDietRoute';
import { UnknownRouteHome } from '@/components/UnknownRouteHome';
import { ModuleKey } from '@/types/featureToggle';
import PageLoader from '@/components/ui/PageLoader';
import { HomeScopeSwitch } from '@/components/home/HomeScopeSwitch';
import { useNavDiet } from '@/config/navDiet';
import { ForcePasswordChangeGate } from '@/components/auth/ForcePasswordChangeGate';
// App-shell chrome + cross-cutting stores. These used to live in App.tsx, which
// rendered them as siblings of <BrowserRouter>. Under a data router only the
// route tree is inside the router context, and NativeShellBridge (useNavigate)
// + PwaPrompts (useLocation) need that context — so the whole shell moves into
// the RootLayout route element below.
import { Toaster } from 'sonner';
import { useTenantStore } from '@/stores/useTenantStore';
import { usePlatformFlag, usePlatformFlagsStore } from '@/config/platformFlags';
import { useAiCapabilitiesStore } from '@/config/aiCapabilities';
import { useFeatureToggleStore } from '@/stores/useFeatureToggleStore';
import { AI_UNAVAILABLE_EVENT } from '@/services/aiErrors';
import { PwaPrompts } from '@/components/pwa/PwaPrompts';
import { NativeShellBridge } from '@/native/NativeShellBridge';
import BetaBanner from '@/components/BetaBanner';
import FeedbackWidget from '@/components/FeedbackWidget';
import ErrorBoundary from '@/components/ErrorBoundary';


// Lazy-loaded Pages
// Design-kit gallery. DEV ONLY.
//
// The ternary is load-bearing: gating only the <Route> leaves this dynamic
// import reachable, so Rollup still emits the chunk and a production build
// ships ~19KB of JS and ~25KB of kit CSS that nothing can navigate to. With
// the import inside a branch that constant-folds to `false`, it is dropped
// from the build entirely.
const KitGalleryPage = import.meta.env.DEV
  ? lazy(() => import('@/pages/dev/kit/KitGalleryPage'))
  : null;
const Dashboard = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.Dashboard })));
const AssistantPage = lazy(() => import('@/pages/assistant/AssistantPage'));
const DoctorHome = lazy(() => import('@/pages/DoctorHome').then(m => ({ default: m.DoctorHome })));
/* The design's Today, bound to real clinic data — the Doctor's home now. The
   previous DoctorHome stays imported above while the other role homes are
   ported; see docs/DESIGN-CHECKLIST.md for what is still on the old shape. */
const DoctorTodayPage = lazy(() => import('@/pages/doctor/today/DoctorTodayPage').then(m => ({ default: m.DoctorTodayPage })));
const DoctorQueuePage = lazy(() => import('@/pages/doctor/today/DoctorQueuePage').then(m => ({ default: m.DoctorQueuePage })));
const DoctorResultsPage = lazy(() => import('@/pages/doctor/results/DoctorResultsPage').then(m => ({ default: m.DoctorResultsPage })));
const DoctorPatientsPage = lazy(() => import('@/pages/doctor/patients/DoctorPatientsPage').then(m => ({ default: m.DoctorPatientsPage })));
const DoctorPatientFilePage = lazy(() => import('@/pages/doctor/file/DoctorPatientFilePage').then(m => ({ default: m.DoctorPatientFilePage })));
const DoctorLibraryPage = lazy(() => import('@/pages/doctor/library/DoctorLibraryPage').then(m => ({ default: m.DoctorLibraryPage })));
const DoctorDirectoryPage = lazy(() => import('@/pages/doctor/directory/DoctorDirectoryPage').then(m => ({ default: m.DoctorDirectoryPage })));
const DoctorIssuePage = lazy(() => import('@/pages/doctor/issue/DoctorIssuePage').then(m => ({ default: m.DoctorIssuePage })));
const DoctorEncounterPage = lazy(() => import('@/pages/doctor/encounter/DoctorEncounterPage').then(m => ({ default: m.DoctorEncounterPage })));
const DoctorQueueHome = lazy(() => import('@/pages/DoctorQueueHome').then(m => ({ default: m.DoctorQueueHome })));
const ReceptionistHome = lazy(() => import('@/pages/ReceptionistHome'));
const NurseHome = lazy(() => import('@/pages/NurseHome'));
const BillingHome = lazy(() => import('@/pages/BillingHome'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const PatientsPage = lazy(() => import('@/pages/patients/PatientsPage').then(m => ({ default: m.PatientsPage })));
const ImportPatientsPage = lazy(() => import('@/pages/patients/ImportPatientsPage').then(m => ({ default: m.ImportPatientsPage })));
const MedicalRecordsPage = lazy(() => import('@/pages/medicalRecords/MedicalRecordsPage').then(m => ({ default: m.MedicalRecordsPage })));
const SchedulePage = lazy(() => import('@/pages/SchedulePage').then(m => ({ default: m.SchedulePage })));
const AddPatientPage = lazy(() => import('@/pages/AddPatientPage').then(m => ({ default: m.AddPatientPage })));
const AddAppointmentPage = lazy(() => import('@/pages/AddAppointmentPage').then(m => ({ default: m.AddAppointmentPage })));
const PatientDetailsPage = lazy(() => import('@/pages/patients/PatientDetailsPage').then(m => ({ default: m.PatientDetailsPage })));
const ResourceSchedulingPage = lazy(() => import('@/pages/scheduling/ResourceSchedulingPage').then(m => ({ default: m.ResourceSchedulingPage })));
const ProviderAvailabilityPage = lazy(() => import('@/pages/scheduling/ProviderAvailabilityPage').then(m => ({ default: m.ProviderAvailabilityPage })));
const WaitlistManagementPage = lazy(() => import('@/pages/scheduling/WaitlistManagementPage').then(m => ({ default: m.WaitlistManagementPage })));
const AddWaitlistPage = lazy(() => import('@/pages/scheduling/AddWaitlistPage').then(m => ({ default: m.AddWaitlistPage })));
const OrderManagementPage = lazy(() => import('@/pages/orders/OrderManagementPage'));
const OrderDetailsPage = lazy(() => import('@/pages/orders/OrderDetailsPage'));
const AppointmentDetailsPage = lazy(() => import('@/pages/AppointmentDetailsPage').then(m => ({ default: m.AppointmentDetailsPage })));
const RecordDetailsPage = lazy(() => import('@/pages/medicalRecords/RecordDetailsPage').then(m => ({ default: m.RecordDetailsPage })));
const EncounterReviewPage = lazy(() => import('@/pages/encounter/EncounterReviewPage').then(m => ({ default: m.EncounterReviewPage })));
const ManualEncounterPage = lazy(() => import('@/pages/encounter/ManualEncounterPage').then(m => ({ default: m.ManualEncounterPage })));
const QuickRecordPage = lazy(() => import('@/pages/voice/QuickRecordPage').then(m => ({ default: m.QuickRecordPage })));
const ClinicLabOrderDetailsPage = lazy(() => import('@/pages/clinicLabOrder/ClinicLabOrderDetailsPage').then(m => ({ default: m.ClinicLabOrderDetailsPage })));
const ClinicLabOrderSessionPage = lazy(() => import('@/pages/clinicLabOrder/ClinicLabOrderSessionPage').then(m => ({ default: m.ClinicLabOrderSessionPage })));
const NewClinicLabOrderPage = lazy(() => import('@/pages/clinicLabOrder/NewClinicLabOrderPage').then(m => ({ default: m.NewClinicLabOrderPage })));
const EditClinicLabOrderPage = lazy(() => import('@/pages/clinicLabOrder/EditClinicLabOrderPage').then(m => ({ default: m.EditClinicLabOrderPage })));
// const ADTPage = lazy(() => import('@/pages/ADTPage').then(m => ({ default: m.ADTPage })));
// const CensusPage = lazy(() => import('@/pages/CensusPage'));
const TemplateLibraryPage = lazy(() => import('@/pages/templates/TemplateLibraryPage').then(m => ({ default: m.TemplateLibraryPage })));
const TemplateBuilderPage = lazy(() => import('@/pages/templates/TemplateBuilderPage').then(m => ({ default: m.TemplateBuilderPage })));
const UseTemplatePage = lazy(() => import('@/pages/templates/UseTemplatePage').then(m => ({ default: m.UseTemplatePage })));
const ProtocolLibraryPage = lazy(() => import('@/pages/protocols/ProtocolLibraryPage').then(m => ({ default: m.ProtocolLibraryPage })));
const ProtocolEditPage = lazy(() => import('@/pages/protocols/ProtocolEditPage').then(m => ({ default: m.ProtocolEditPage })));
const CPOEPage = lazy(() => import('@/pages/CPOEPage'));
const MedicationOrderForm = lazy(() => import('@/components/MedicationOrderForm'));
const ImagingOrderForm = lazy(() => import('@/components/ImagingOrderForm'));
const OrderSetsManager = lazy(() => import('@/components/OrderSetsManager'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const RegisterCompletePage = lazy(() => import('@/pages/auth/RegisterCompletePage'));
const SsoDecidePage = lazy(() => import('@/pages/auth/SsoDecidePage'));
const CreateClinicPage = lazy(() => import('@/pages/CreateClinicPage'));
const UserManagementPage = lazy(() => import('@/pages/admin/UserManagementPage'));
const MembersPage = lazy(() => import('@/pages/admin/MembersPage'));
const ExternalProvidersDirectoryPage = lazy(() => import('@/pages/admin/ExternalProvidersDirectoryPage'));
const WelcomePage = lazy(() => import('@/pages/WelcomePage'));
const ExplorePage = lazy(() => import('@/pages/ExplorePage'));
const SsoHandoffPage = lazy(() => import('@/pages/SsoHandoffPage'));
const InviteAcceptPage = lazy(() => import('@/pages/InviteAcceptPage'));
const RoleManagementPage = lazy(() => import('@/pages/admin/RoleManagementPage'));
const SystemSettingsPage = lazy(() => import('@/pages/admin/SystemSettingsPage'));
const SystemMonitoringPage = lazy(() => import('@/pages/admin/SystemMonitoringPage'));
const AuditLogsPage = lazy(() => import('@/pages/admin/AuditLogsPage'));
const FeatureTogglePage = lazy(() => import('@/pages/admin/FeatureTogglePage'));
const SubscriptionPage = lazy(() => import('@/pages/admin/SubscriptionPage'));
const CheckoutResultPage = lazy(() => import('@/pages/admin/CheckoutResultPage'));
const ChannelsPage = lazy(() => import('@/pages/ChannelsPage'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const DomainsPage = lazy(() => import('@/pages/admin/DomainsPage'));
const ClinicLabOrdersPage = lazy(() => import('@/pages/clinicLabOrder/ClinicLabOrdersPage'));
const ImagingOrdersPage = lazy(() => import('@/pages/ImagingOrdersPage'));
const ReferralsListPage = lazy(() => import('@/pages/ReferralsListPage'));
const ClinicSettingsPage = lazy(() => import('@/pages/clinic/ClinicSettingsPage'));
const YourClinicPanel = lazy(() => import('@/pages/clinic/YourClinicPanel'));
const OrdersPage = lazy(() => import('@/pages/orders/OrdersPage'));
const BranchesPanel = lazy(() => import('@/pages/clinic/panels').then(m => ({ default: m.BranchesPanel })));
const DepartmentsPanel = lazy(() => import('@/pages/clinic/panels').then(m => ({ default: m.DepartmentsPanel })));
const SystemHealthPanel = lazy(() => import('@/pages/clinic/panels').then(m => ({ default: m.SystemHealthPanel })));
const KnowledgeBasePage = lazy(
  () => import('@/pages/admin/KnowledgeBasePage'),
);
const InboxPage = lazy(() => import('@/pages/InboxPage'));
const BillingPage = lazy(() => import('@/pages/billing/BillingPage').then(m => ({ default: m.BillingPage })));
const BillDetailsPage = lazy(() => import('@/pages/billing/BillDetailsPage').then(m => ({ default: m.BillDetailsPage })));
const InvoiceFormPage = lazy(() => import('@/pages/billing/InvoiceFormPage').then(m => ({ default: m.InvoiceFormPage })));
const TestPage = lazy(() => import('@/pages/TestPage').then(m => ({ default: m.TestPage })));
const PharmacyPage = lazy(() => import('@/pages/pharmacy/PharmacyPage'));
const PatientMARPage = lazy(() => import('@/pages/pharmacy/PatientMARPage'));
// const BedManagementPage = lazy(() => import('@/pages/hospital/BedManagementPage').then(m => ({ default: m.BedManagementPage })));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const PrescriptionDetailsPage = lazy(() => import('@/pages/PrescriptionDetailsPage'));
const PrescriptionSessionPage = lazy(() => import('@/pages/PrescriptionSessionPage'));
const NewPrescriptionPage = lazy(() => import('@/pages/NewPrescriptionPage'));
const PrescriptionsListPage = lazy(() => import('@/pages/PrescriptionsListPage'));
const RefillRequestsPage = lazy(() => import('@/pages/RefillRequestsPage'));
const DrugsPage = lazy(() => import('@/pages/DrugsPage'));
const MedicationsPage = lazy(() => import('@/pages/MedicationsPage').then(m => ({ default: m.MedicationsPage })));
const ImportMedicationsPage = lazy(() => import('@/pages/ImportMedicationsPage'));
const ImportPrescriptionsPage = lazy(() => import('@/pages/ImportPrescriptionsPage'));
const ImagingOrderSessionPage = lazy(() => import('@/pages/ImagingOrderSessionPage').then(m => ({ default: m.ImagingOrderSessionPage })));
const ScanDocumentPage = lazy(() => import('@/pages/ScanDocumentPage').then(m => ({ default: m.ScanDocumentPage })));
const ImagingOrderDetailsPage = lazy(() => import('@/pages/ImagingOrderDetailsPage').then(m => ({ default: m.ImagingOrderDetailsPage })));
const NewImagingOrderPage = lazy(() => import('@/pages/NewImagingOrderPage').then(m => ({ default: m.NewImagingOrderPage })));
const DicomSpikePage = lazy(() => import('@/pages/DicomSpikePage').then(m => ({ default: m.DicomSpikePage })));
const MobileScribeCapturePage = lazy(() => import('@/pages/MobileScribeCapturePage'));
const RxVerifyPage = lazy(() => import('@/pages/RxVerifyPage'));
const DocumentVerifyPage = lazy(() => import('@/pages/DocumentVerifyPage'));
const ReferralDetailsPage = lazy(() => import('@/pages/ReferralDetailsPage'));
const OrderCatalogPage = lazy(() => import('@/pages/OrderCatalogPage').then(m => ({ default: m.OrderCatalogPage })));

/* Dev-only, and the ternary is what keeps it that way: the import sits inside
   the branch, so a production build constant-folds the whole thing to null and
   Rollup never follows it into a chunk. Same shape as KitGalleryPage above —
   see the note there about why that matters. */
const DesignApp = import.meta.env.DEV
  ? lazy(() => import('@/pages/design/DesignApp').then((m) => ({ default: m.DesignApp })))
  : null;


/**
 * Role-aware home dispatcher. The same `/home` URL renders a different
 * landing page per role so each member opens straight into their day's
 * work. Owner falls through to the doctor view (clinic founder is
 * usually a doctor too); we can split that out later if owners ask.
 */
const HomeByRole: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const queueHome = usePlatformFlag('DOCTOR_QUEUE_HOME_ENABLED');
  /* `/today` is a DESIGN route. Sending a browser that is not on the design
     shell there lands a kit page inside the old chrome, which consumes none of
     the title, search, actions or pane the page publishes — so the doctor's
     home became a headless fragment with no way back. Off the flag, home is
     what home was. */
  const designShell = useMaieShell();
  /* A ROLE'S OWN FIRST DOOR, once it has one. Under the design shell, a role
     whose module in src/pages/roles/ declares where its first door goes lands
     there; one that has not yet keeps the home it had, rather than being sent
     into another role's screen by a shared path. */
  const { surface } = useShellIdentity();
  const firstDoor = DESIGN_ROLES[surface]?.doors?.[0];
  const ownHome =
    designShell && surface !== 'doctor' && firstDoor
      ? doorRouteFor(surface, firstDoor.key)
      : undefined;
  const [searchParams] = useSearchParams();

  // Dashboard was never a different place — it was the same day at clinic
  // scope. It is a toggle on this page now, offered only to the roles that
  // hold report:read (everyone else would 403 on the reports endpoints).
  // Only under the diet: the pre-diet sidebar still carries its own Dashboard
  // row, and offering both would be two doors to one room.
  const { navDiet } = useNavDiet();
  const canSeeClinicScope =
    navDiet && roleCheck.hasPermission(Permission.REPORT_READ);
  const clinicScope =
    canSeeClinicScope && searchParams.get('scope') === 'clinic';

  const home = (() => {
    if (ownHome) return <Navigate to={ownHome} replace />;
    switch (user?.primaryRole) {
      case UserRole.NURSE:
        return <NurseHome />;
      case UserRole.RECEPTIONIST:
        return <ReceptionistHome />;
      case UserRole.BILLING:
      case UserRole.BILLING_STAFF:
        return <BillingHome />;
      default:
      // DOCTOR_QUEUE_HOME_ENABLED swaps the doctor's landing page for the
      // single queue column. Branching HERE rather than inside DoctorHome
      // is deliberate: that component runs ~30 hooks, so an early return
      // would change hook order the moment the flag flipped mid-session.
      // Off renders the existing home untouched.
      // The design's Today is the doctor's home now and goes first: it
      // replaced the queue home, so a clinic that had opted into the queue
      // home must not keep a design-shell doctor parked on /home.
        if (designShell) return <Navigate to="/today" replace />;
        if (queueHome) return <DoctorQueueHome />;
        return <DoctorHome />;
    }
  })();

  return (
    <>
      {canSeeClinicScope && <HomeScopeSwitch />}
      {clinicScope ? <Dashboard /> : home}
    </>
  );
};

/**
 * Root layout route element. This is the single element mounted at the top of
 * the data-router tree, so it's the home for everything that must run on every
 * route regardless of auth: the app-shell chrome (moved here from the old
 * App.tsx), the cross-cutting bootstrap effects, and the branding/auth loading
 * gate. Children render through <Outlet />; while branding is still resolving
 * for an authenticated session we show the spinner in the Outlet's place (the
 * chrome stays mounted, matching the old App.tsx behaviour).
 */
function RootLayout() {
    /* The design shell has no feedback bubble — see where it is used below. */
    const onDesignShell = useMaieShell();
    const { isLoading } = useTheme();
    const { isAuthenticated } = useAuthStore();

    // Drive sonner's base theme off the user's resolved colour scheme so
    // toast chrome (close button, base surface) matches dark vs light.
    // The neutral toast surface itself is bound to the app's --color-*
    // tokens below, so it tracks the active tenant palette exactly.
    const colorScheme = useAccessibilityStore((s) => s.prefs.colorScheme);
    const sonnerTheme = isEffectiveDarkScheme(colorScheme) ? 'dark' : 'light';

    const { branding } = useThemeStore();

    // App-shell bootstrap (relocated verbatim from App.tsx).
    const resolveForCurrentHost = useTenantStore((s) => s.resolveForCurrentHost);
    const refreshMemberships = useAuthStore((s) => s.refreshMemberships);
    const refreshPlatformFlags = usePlatformFlagsStore((s) => s.refresh);
    const refreshAiCapabilities = useAiCapabilitiesStore((s) => s.refresh);
    const refreshModules = useFeatureToggleStore((s) => s.refresh);

    // Resolve tenant branding + IdPs from the current host before the
    // login page renders. Runs once per mount.
    useEffect(() => {
        resolveForCurrentHost();
    }, [resolveForCurrentHost]);

    // When the user is authenticated, populate memberships + merged platform
    // flags + per-feature AI availability so the header switcher, beta
    // exemptions, and AI affordances all resolve without a per-navigation
    // round trip.
    useEffect(() => {
        if (isAuthenticated) {
            refreshMemberships();
            refreshPlatformFlags();
            refreshAiCapabilities();
            // Which modules this clinic runs. Drives FeatureGuard, the
            // sidebar and ExplorePage — so it has to land before the nav
            // means anything.
            refreshModules();
        }
    }, [
        isAuthenticated,
        refreshMemberships,
        refreshPlatformFlags,
        refreshAiCapabilities,
        refreshModules,
    ]);

    // Self-heal: when any AI call returns an availability error, re-pull the
    // capability map so the UI flips affordances to disabled without a reload.
    useEffect(() => {
        const onUnavailable = () => {
            if (useAuthStore.getState().isAuthenticated) refreshAiCapabilities();
        };
        window.addEventListener(AI_UNAVAILABLE_EVENT, onUnavailable);
        return () => window.removeEventListener(AI_UNAVAILABLE_EVENT, onUnavailable);
    }, [refreshAiCapabilities]);

    // Initialize localization from backend on mount. Theme/branding is
    // loaded via React Query in useTheme() above — no need to fetch twice.
    useEffect(() => {
        const localizationStore = useLocalizationStore.getState();
        localizationStore.loadFromStorage();
    }, []);

    // Dynamic page title and favicon
    useEffect(() => {
        if (branding.hospitalName) {
            document.title = `${branding.hospitalName} - EMR`;
        }
        if (branding.favicon) {
            let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.head.appendChild(link);
            }
            link.href = branding.favicon;
        }
    }, [branding.hospitalName, branding.favicon]);

    // Initialize mock data
    useEffect(() => {
        const cdsStore = useClinicalDecisionSupportStore.getState();
        const cpoeStore = useCPOEStore.getState();
        const adtStore = useADTStore.getState();

        // Initialize CDS mock data (drugs, interactions, allergies)
        if (cdsStore.drugs.length === 0) {
            cdsStore.generateMockData();
        }

        // E-Prescribing medications are now fetched from RxNorm API via searchMedications()

        // Add mock CPOE medication orders
        if (cpoeStore.orders.size === 0) {
            const mockCPOEOrders = [
                {
                    id: 'med-1',
                    patientId: '1',
                    type: 'medication' as const,
                    status: 'active' as const,
                    priority: 'routine' as const,
                    medication: {
                        genericName: 'Albuterol',
                        brandName: 'Ventolin',
                        rxcui: '435',
                        strength: '90mcg'
                    },
                    dosage: {
                        amount: 2,
                        unit: 'puffs'
                    },
                    route: 'inhalation',
                    frequency: {
                        code: 'q4-6h',
                        display: 'Every 4-6 hours as needed'
                    },
                    indication: {
                        en: 'Asthma exacerbation',
                        ar: 'تفاقم الربو'
                    },
                    orderedBy: {
                        id: 'doc-1',
                        name: 'Dr. Smith',
                        title: 'MD'
                    },
                    orderedAt: new Date(),
                    startDate: new Date()
                },
                {
                    id: 'med-2',
                    patientId: '2',
                    type: 'medication' as const,
                    status: 'active' as const,
                    priority: 'routine' as const,
                    medication: {
                        genericName: 'Lisinopril',
                        brandName: 'Prinivil',
                        rxcui: '29046',
                        strength: '10mg'
                    },
                    dosage: {
                        amount: 10,
                        unit: 'mg'
                    },
                    route: 'oral',
                    frequency: {
                        code: 'qd',
                        display: 'Once daily'
                    },
                    indication: {
                        en: 'Hypertension',
                        ar: 'ارتفاع ضغط الدم'
                    },
                    orderedBy: {
                        id: 'doc-2',
                        name: 'Dr. Johnson',
                        title: 'MD'
                    },
                    orderedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
                    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                },
                {
                    id: 'med-3',
                    patientId: '4',
                    type: 'medication' as const,
                    status: 'active' as const,
                    priority: 'routine' as const,
                    medication: {
                        genericName: 'Warfarin',
                        brandName: 'Coumadin',
                        rxcui: '11289',
                        strength: '5mg'
                    },
                    dosage: {
                        amount: 5,
                        unit: 'mg'
                    },
                    route: 'oral',
                    frequency: {
                        code: 'qd',
                        display: 'Once daily'
                    },
                    indication: {
                        en: 'Atrial fibrillation prophylaxis',
                        ar: 'الوقاية من الرجفان الأذيني'
                    },
                    orderedBy: {
                        id: 'doc-3',
                        name: 'Dr. Wilson',
                        title: 'MD'
                    },
                    orderedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                    notes: 'Monitor INR weekly. Target range 2.0-3.0'
                }
            ];
            mockCPOEOrders.forEach(order => cpoeStore.addOrder(order as Parameters<typeof cpoeStore.addOrder>[0]));
        }

        // Add mock admissions (encounters)
        if (adtStore.admissions.length === 0) {
            const mockAdmissions = [
                {
                    id: 'adm-001',
                    patientId: '1', // Sarah Johnson
                    admissionDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    admissionTime: '14:30',
                    admittingPhysicianId: 'doc-smith',
                    type: 'emergency' as const,
                    status: 'active' as const,
                    chiefComplaint: {
                        en: 'Severe asthma exacerbation with shortness of breath',
                        ar: 'تفاقم حاد في الربو مع ضيق في التنفس'
                    },
                    diagnosis: {
                        en: 'Acute asthma exacerbation',
                        ar: 'تفاقم حاد في الربو'
                    },
                    bedId: 'bed-201',
                    roomId: 'room-201',
                    wardId: 'ward-2',
                    insuranceVerified: true,
                    insuranceDetails: {
                        provider: 'Blue Cross',
                        policyNumber: 'BC123456',
                        verificationDate: new Date().toISOString()
                    },
                    wristbandPrinted: true,
                    notificationsSent: ['nursing', 'pharmacy', 'respiratory']
                },
                {
                    id: 'adm-002',
                    patientId: '2', // Michael Chen
                    admissionDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    admissionTime: '09:15',
                    admittingPhysicianId: 'doc-johnson',
                    type: 'elective' as const,
                    status: 'active' as const,
                    chiefComplaint: {
                        en: 'Scheduled diabetes management and blood pressure control',
                        ar: 'إدارة مرض السكري والتحكم في ضغط الدم المجدولة'
                    },
                    diagnosis: {
                        en: 'Type 2 Diabetes Mellitus, Hypertension',
                        ar: 'داء السكري من النوع 2، ارتفاع ضغط الدم'
                    },
                    bedId: 'bed-105',
                    roomId: 'room-105',
                    wardId: 'ward-1',
                    insuranceVerified: true,
                    insuranceDetails: {
                        provider: 'Aetna',
                        policyNumber: 'AET789012',
                        verificationDate: new Date().toISOString()
                    },
                    wristbandPrinted: true,
                    notificationsSent: ['nursing', 'dietary', 'pharmacy']
                },
                {
                    id: 'adm-003',
                    patientId: '3', // Emily Rodriguez
                    admissionDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    admissionTime: '18:45',
                    admittingPhysicianId: 'doc-wilson',
                    type: 'emergency' as const,
                    status: 'active' as const,
                    chiefComplaint: {
                        en: 'Severe migraine with visual disturbances and nausea',
                        ar: 'صداع نصفي حاد مع اضطرابات بصرية وغثيان'
                    },
                    diagnosis: {
                        en: 'Acute migraine with aura',
                        ar: 'صداع نصفي حاد مع هالة'
                    },
                    bedId: 'bed-308',
                    roomId: 'room-308',
                    wardId: 'ward-3',
                    insuranceVerified: true,
                    wristbandPrinted: true,
                    notificationsSent: ['nursing', 'pharmacy']
                },
                {
                    id: 'adm-004',
                    patientId: '4', // James Wilson
                    admissionDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    admissionTime: '11:20',
                    admittingPhysicianId: 'doc-brown',
                    type: 'elective' as const,
                    status: 'active' as const,
                    chiefComplaint: {
                        en: 'Rheumatoid arthritis flare-up requiring joint injection',
                        ar: 'نوبة التهاب المفاصل الروماتويدي تتطلب حقن المفصل'
                    },
                    diagnosis: {
                        en: 'Rheumatoid arthritis, bilateral knee pain',
                        ar: 'التهاب المفاصل الروماتويدي، ألم الركبة الثنائي'
                    },
                    bedId: 'bed-412',
                    roomId: 'room-412',
                    wardId: 'ward-4',
                    insuranceVerified: true,
                    insuranceDetails: {
                        provider: 'Medicare',
                        policyNumber: 'MED345678',
                        verificationDate: new Date().toISOString()
                    },
                    wristbandPrinted: true,
                    notificationsSent: ['nursing', 'physical-therapy']
                },
                {
                    id: 'adm-005',
                    patientId: '5', // Maria Garcia
                    admissionDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    admissionTime: '13:00',
                    admittingPhysicianId: 'doc-martinez',
                    type: 'elective' as const,
                    status: 'active' as const,
                    chiefComplaint: {
                        en: 'Thyroid function monitoring and osteoporosis management',
                        ar: 'مراقبة وظائف الغدة الدرقية وإدارة هشاشة العظام'
                    },
                    diagnosis: {
                        en: 'Hypothyroidism, Osteoporosis',
                        ar: 'قصور الغدة الدرقية، هشاشة العظام'
                    },
                    bedId: 'bed-215',
                    roomId: 'room-215',
                    wardId: 'ward-2',
                    insuranceVerified: true,
                    insuranceDetails: {
                        provider: 'United Healthcare',
                        policyNumber: 'UHC567890',
                        verificationDate: new Date().toISOString()
                    },
                    wristbandPrinted: true,
                    notificationsSent: ['nursing', 'dietary', 'pharmacy']
                },
                {
                    id: 'adm-006',
                    patientId: '1', // Sarah Johnson - Second admission
                    admissionDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    admissionTime: '10:15',
                    admittingPhysicianId: 'doc-smith',
                    type: 'emergency' as const,
                    status: 'discharged' as const,
                    chiefComplaint: {
                        en: 'Asthma attack with respiratory distress',
                        ar: 'نوبة ربو مع ضائقة تنفسية'
                    },
                    diagnosis: {
                        en: 'Acute asthma exacerbation',
                        ar: 'تفاقم حاد في الربو'
                    },
                    insuranceVerified: true,
                    wristbandPrinted: true,
                    notificationsSent: ['nursing', 'respiratory']
                }
            ];
            mockAdmissions.forEach(admission => adtStore.addAdmission(admission));
        }
    }, []);


    // Branding/auth loading gate. `isLoading` is only ever true for an
    // authenticated session whose theme config hasn't resolved yet — pre-auth
    // there's nothing to wait for. We render the spinner in the Outlet's place
    // (rather than as a bare early return) so the chrome stays mounted, and so
    // that on a deep-link cold load an authenticated session shows the branded
    // spinner instead of flashing the login page while branding hydrates.
    const loadingBranding = useThemeStore.getState().branding;
    const outletArea = isLoading ? (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
            <div className="text-center">
                {loadingBranding.logo && (
                    <img src={loadingBranding.logo} alt={loadingBranding.hospitalName} className="h-12 sm:h-16 mx-auto mb-3 sm:mb-4 object-contain" />
                )}
                <div
                    className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 mx-auto mb-3 sm:mb-4"
                    style={{ borderColor: 'var(--color-primary-600)' }}
                ></div>
                <p className="text-sm sm:text-base font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    {loadingBranding.hospitalName}
                </p>
                <p className="text-xs sm:text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Loading...
                </p>
            </div>
        </div>
    ) : (
        // One Suspense boundary for every lazy route under the tree. A lazy
        // page suspending replaces the whole Outlet (incl. MainLayout) with the
        // PageLoader, exactly as the old top-level <Suspense> did.
        <Suspense fallback={<PageLoader />}>
            <Outlet />
        </Suspense>
    );

    return (
        <>
            <BetaBanner />
            <ErrorBoundary>{outletArea}</ErrorBoundary>
            {/* ON NO BOARD, AND IT SAT ON THE LAST ROW of every list at
                (293, 977). Three lanes hid it themselves; the shell does it
                once. Feedback belongs in the Account menu on this shell. */}
            {onDesignShell ? null : <FeedbackWidget />}
            <NativeShellBridge />
            <PwaPrompts />
            {/* Data-router-only scroll restoration. Harmless if nothing relied
                on it; restores the browser-default scroll behaviour across
                client navigations now that we own routing. */}
            <ScrollRestoration />
            <Toaster
                // Toasts pinned top-right so they never collide with the
                // bottom-right Encounter / Ask MAIE FAB or the bottom-left
                // FeedbackWidget.
                position="top-right"
                richColors
                closeButton
                theme={sonnerTheme}
                duration={3000}
                toastOptions={{
                    // Map sonner's neutral-toast custom properties to the
                    // app's theme tokens so default toasts (e.g. the PWA
                    // "new version available" reload prompt) match the
                    // active palette in both light and dark instead of
                    // sonner's generic white/grey. richColors still owns
                    // success/error/warning surfaces.
                    style: {
                        borderRadius: '12px',
                        padding: '16px',
                        '--normal-bg': 'var(--color-bg-primary)',
                        '--normal-text': 'var(--color-text-primary)',
                        '--normal-border': 'var(--color-border-primary)',
                    } as React.CSSProperties,
                }}
            />
        </>
    );
}

/**
 * Auth gate for the signed-in app. Reads the same Zustand auth store the old
 * conditional tree used. Not authenticated → bounce to /login; because the "/"
 * MainLayout branch (and its `*` catch-all) lives under here, this is also what
 * sends any unknown path to /login while signed out. Authenticated → mount the
 * mandatory change-password overlay, the route subtree, and the floating
 * assistant panel — all formerly siblings of the authed <Routes>.
 */
function RequireAuth() {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    return (
        <>
            {/* Mandatory change-password gate — overlays the whole app when
                the user is on an owner-issued temporary password. */}
            <ForcePasswordChangeGate />
            <Outlet />
            <AIAssistantPanel
                isOpen={false}
                onClose={() => {}}
                contexts={[]}
                onRemoveContext={() => {}}
                onClearAllContexts={() => {}}
                onAddFullPageContext={() => {}}
                isListening={false}
                onToggleListening={() => {}}
            />
        </>
    );
}

/**
 * Inverse gate for the public auth pages. An already-authenticated user has no
 * business on login/register/forgot/sso, so we send them home — the old code
 * achieved the same by never mounting these routes once authenticated (they
 * fell through to the authed catch-all). "/" resolves to the role home via the
 * MainLayout index route.
 */
function RequireAnon() {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }
    return <Outlet />;
}

/**
 * The application's route tree, built once at module load. Expressed with
 * createRoutesFromElements so the ~120 <Route> elements carry over almost
 * verbatim from the old conditional <Routes> trees. The auth/loading gating
 * that used to be React-state branches is now structural: RootLayout (always),
 * the guard-free dual-reachable routes, RequireAnon (public-only), and
 * RequireAuth (the signed-in app).
 */
export const appRoutes = createRoutesFromElements(
    <Route element={<RootLayout />}>
        {/* Dual-reachable: intentionally public AND usable while signed in, so
            they carry no auth guard. Preserves the old behaviour where each
            appeared in BOTH the public and authed trees. */}
        {/* Phone-as-mic capture. Public — the URL carries a short-lived pairing
            code redeemed for a scoped token, so no clinic login is required;
            also reachable signed-in to test on a paired device. */}
        <Route path="/m/scribe/:code" element={<MobileScribeCapturePage />} />
        {/* Public Rx verify — a pharmacist scanning a QR signed-out must not be
            bounced to login, and a doctor's own scan from inside the EMR
            shouldn't route through the auth-aware layout either. */}
        <Route path="/rx/verify/:token" element={<RxVerifyPage />} />
        {/* Public certificate check — whoever holds the paper types its code +
            issue date; same no-auth reasoning as Rx verify. */}
        <Route path="/verify" element={<DocumentVerifyPage />} />
        <Route path="/verify/:code" element={<DocumentVerifyPage />} />
        {/* Design-kit gallery. DEV BUILDS ONLY (the lazy import constant-folds
            away in prod, so neither the route nor its chunk ships). Public on
            purpose: it renders components, touches no clinic data, and putting
            a dev tool behind a login is how it stops getting opened. */}
        {KitGalleryPage && <Route path="/dev/kit" element={<KitGalleryPage />} />}
        {/* THE DESIGN, MOUNTED AS THE APPLICATION. Dev builds only, same as the
            gallery — the lazy import constant-folds away in prod so neither the
            route nor its chunk ships.

            These are the ported canvases running full-screen at real URLs with
            a working rail, which is the only way to judge the design as a shell
            rather than as pictures. They carry the CANVAS'S sample content, not
            this clinic's — nothing here touches the API. Wiring is per screen
            and comes next. */}
        {DesignApp && <Route path="/design/:role?/:door?" element={<DesignApp />} />}
        {/* Invite-accept: reachable signed-out (brand-new user) and signed-in
            (a user already in another tenant redeeming a fresh invite link). */}
        <Route path="/invitations/:token/accept" element={<InviteAcceptPage />} />

        {/* Public-only auth surfaces — redirect home if already signed in. */}
        <Route element={<RequireAnon />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register/complete" element={<RegisterCompletePage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/sso/decide" element={<SsoDecidePage />} />
            <Route path="/sso" element={<SsoHandoffPage />} />
        </Route>

        {/* Signed-in application — RequireAuth bounces to /login otherwise. */}
        <Route element={<RequireAuth />}>
            {/* First-run demo consult lives outside MainLayout so fresh
                signups get one focused, undistracted screen. */}
            <Route path="/welcome" element={<WelcomePage />} />


            {/* Create-another-clinic. Standalone form on the shared host;
                redirects you there from a tenant subdomain because signup
                creates a new tenant. */}
            <Route path="/clinics/new" element={<CreateClinicPage />} />

            {/* MAIE-only platform admin lives in the standalone MAIE.Console
                app. The clinician EMR no longer carries any cross-tenant code
                or routes — see docs/CONSOLE_PLAN.md §6. */}

            <Route path="/" element={<MainLayout />}>
                        <Route index element={<Navigate to="/home" replace />} />
                        <Route path="home" element={<HomeByRole />} />
                        {/* The pre-design doctor home, kept reachable while the
                            port lands — a doctor who needs something not yet
                            ported has somewhere to go, rather than a missing
                            feature and no route to it. */}
                        <Route path="home/legacy" element={<DoctorHome />} />
                        {/* The ported design screens. They live INSIDE the
                            layout like every other route: the shell owns the
                            rail, top bar and pane, and each page publishes what
                            its chrome contains (see layouts/shellChrome). That
                            is what keeps one shell across ported and un-ported
                            routes alike. */}
                        {/* Every role's own pages, each under its own `/<role>/` prefix.
                            Declared in src/pages/roles/<role>/index.tsx, never here —
                            see src/pages/roles/types.ts for why. */}
                        {ROLE_ROUTES.map((r) => (
                          <Route
                            key={r.path}
                            path={r.path.replace(/^\//, '')}
                            element={<RoleRouteGuard path={r.path}>{r.element}</RoleRouteGuard>}
                          />
                        ))}
                        <Route path="today" element={<DoctorTodayPage />} />
                        <Route path="queue" element={<DoctorQueuePage />} />
                        <Route path="results" element={<DoctorResultsPage />} />
                        {/* The design replaced this as the DOCTOR'S day — homeRoute
                            sends them to their own Today and nothing in their rail
                            points here any more. It is still the CLINIC's day, which
                            is the destination behind the folded manager hat, so under
                            the design shell it must render rather than bounce: with
                            `maieTo="/home"` it redirected to /home, /home resolved by
                            role back to /today, and the manager's Today led to the
                            doctor's. */}
                        <Route path="dashboard" element={<NavDietRoute to="/home?scope=clinic"><Dashboard /></NavDietRoute>} />
                        <Route path="assistant" element={<AssistantPage />} />
                        {/* Orientation: guided next-step + the feature map. */}
                        <Route path="explore" element={<ExplorePage />} />
                        {/* The design's Patients list. The previous page stays
                            at /patients/legacy while the file and the add form
                            are still on the old shape. */}
                        <Route path="patients" element={<FeatureGuard moduleKey={ModuleKey.PATIENT_MANAGEMENT}><ShellRoute design={<DoctorPatientsPage />} legacy={<PatientsPage />} /></FeatureGuard>} />
                        <Route path="patients/legacy" element={<FeatureGuard moduleKey={ModuleKey.PATIENT_MANAGEMENT}><PatientsPage /></FeatureGuard>} />
                        {/* The design's file — one surface, four tabs. The
                            previous chart stays at .../legacy because the
                            encounter, the attachment upload and the issue menu
                            still live on it. */}
                        <Route path="patients/:patientId" element={<FeatureGuard moduleKey={ModuleKey.PATIENT_MANAGEMENT}><ShellRoute design={<DoctorPatientFilePage />} legacy={<PatientDetailsPage />} /></FeatureGuard>} />
                        <Route path="patients/:patientId/legacy" element={<FeatureGuard moduleKey={ModuleKey.PATIENT_MANAGEMENT}><PatientDetailsPage /></FeatureGuard>} />
                        {/* The file header's second action — see DoctorIssuePage. */}
                        <Route path="patients/:patientId/issue" element={<FeatureGuard moduleKey={ModuleKey.PATIENT_MANAGEMENT}><DoctorIssuePage /></FeatureGuard>} />
                        <Route path="patients/new" element={<FeatureGuard moduleKey={ModuleKey.PATIENT_MANAGEMENT}><AddPatientPage /></FeatureGuard>} />
                        <Route path="patients/import" element={<FeatureGuard moduleKey={ModuleKey.PATIENT_MANAGEMENT}><ImportPatientsPage /></FeatureGuard>} />
                        <Route path="patients/:patientId/edit" element={<FeatureGuard moduleKey={ModuleKey.PATIENT_MANAGEMENT}><AddPatientPage /></FeatureGuard>} />
                        <Route path="patients/:patientId/timeline" element={<PatientTimelineRedirect />} />
                        <Route path="schedule" element={<FeatureGuard moduleKey={ModuleKey.APPOINTMENTS}><SchedulePage /></FeatureGuard>} />
                        <Route path="resource-scheduling" element={<FeatureGuard moduleKey={ModuleKey.APPOINTMENTS}><ResourceSchedulingPage /></FeatureGuard>} />
                        <Route path="provider-availability" element={<FeatureGuard moduleKey={ModuleKey.APPOINTMENTS}><ProviderAvailabilityPage /></FeatureGuard>} />
                        <Route path="waitlist-management" element={<FeatureGuard moduleKey={ModuleKey.APPOINTMENTS}><WaitlistManagementPage /></FeatureGuard>} />
                        <Route path="add-waitlist" element={<FeatureGuard moduleKey={ModuleKey.APPOINTMENTS}><AddWaitlistPage /></FeatureGuard>} />
                        <Route path="order-management" element={<OrderManagementPage />} />
                        <Route path="appointments/:appointmentId" element={<FeatureGuard moduleKey={ModuleKey.APPOINTMENTS}><AppointmentDetailsPage /></FeatureGuard>} />
                        <Route path="appointments/new" element={<FeatureGuard moduleKey={ModuleKey.APPOINTMENTS}><AddAppointmentPage /></FeatureGuard>} />
                        <Route path="appointments/:appointmentId/edit" element={<FeatureGuard moduleKey={ModuleKey.APPOINTMENTS}><AddAppointmentPage /></FeatureGuard>} />
                        {/* Medical records are core EMR — not gated behind a
                            module toggle. Permissions are enforced server-side
                            on every read/write. */}
                        <Route path="records" element={<MedicalRecordsPage />} />
                        {/* Clinic-wide referrals management (self-scoped server-side:
                            doctors see own, managers/front-desk see all). Core EMR,
                            not module-gated; backend enforces referral:read. */}
                        <Route path="referrals" element={<NavDietRoute to="/orders?type=referrals"><ReferralsListPage /></NavDietRoute>} />
                        <Route path="referrals/:referralId" element={<ReferralDetailsPage />} />
                        {/* Legacy route — encounter creation lives on the patient page now (see EncounterOverlay). */}
                        <Route path="records/new" element={<Navigate to="/patients" replace />} />
                        <Route path="records/:recordId" element={<RecordDetailsPage />} />
                        <Route path="encounter/new/manual" element={<ManualEncounterPage />} />
                        {/* THE NOTE. Every "Continue visit · draft" and "Open
                            the note" points here, and until now there was no
                            such route — only `.../review`. */}
                        <Route path="encounter/:recordId" element={<DoctorEncounterPage />} />
                        <Route path="encounter/:recordId/review" element={<EncounterReviewPage />} />
                        {/* Voice-trigger entry points — wired to PWA manifest shortcuts,
                            iOS App Intents (Siri), and Android App Actions (Assistant). */}
                        <Route path="quick-record" element={<QuickRecordPage />} />
                        <Route path="encounter/quick-record" element={<Navigate to="/quick-record" replace />} />
                        <Route path="encounter/new" element={<Navigate to="/quick-record" replace />} />
                        {/* Complex lab orders routes temporarily disabled - using simplified clinic lab orders instead */}
                        {/* <Route path="lab-orders" element={<FeatureGuard moduleKey={ModuleKey.LAB_ORDERS}><LabOrdersPage /></FeatureGuard>} />
          <Route path="lab-orders/:orderId" element={<FeatureGuard moduleKey={ModuleKey.LAB_ORDERS}><LabOrderDetailsPage /></FeatureGuard>} />
          <Route path="lab-orders/new" element={<FeatureGuard moduleKey={ModuleKey.LAB_ORDERS}><NewLabOrderPage /></FeatureGuard>} />
          <Route path="patients/:patientId/lab-orders/new" element={<FeatureGuard moduleKey={ModuleKey.LAB_ORDERS}><NewLabOrderPage /></FeatureGuard>} /> */}
                        {/* Simplified clinic lab orders */}
                        <Route path="lab-orders" element={<NavDietRoute to="/orders?type=labs"><FeatureGuard moduleKey={ModuleKey.LAB_ORDERS}><ClinicLabOrdersPage /></FeatureGuard></NavDietRoute>} />
                        <Route path="lab-orders/session/:groupId" element={<FeatureGuard moduleKey={ModuleKey.LAB_ORDERS}><ClinicLabOrderSessionPage /></FeatureGuard>} />
                        <Route path="lab-orders/new" element={<FeatureGuard moduleKey={ModuleKey.LAB_ORDERS}><NewClinicLabOrderPage /></FeatureGuard>} />
                        <Route path="lab-orders/:orderId" element={<FeatureGuard moduleKey={ModuleKey.LAB_ORDERS}><ClinicLabOrderDetailsPage /></FeatureGuard>} />
                        <Route path="lab-orders/:orderId/edit" element={<FeatureGuard moduleKey={ModuleKey.LAB_ORDERS}><EditClinicLabOrderPage /></FeatureGuard>} />
                        <Route path="patients/:patientId/mar" element={<PatientMARPage />} />
                        {/* Pharmacy umbrella — collapses the four formerly
                            separate sidebar entries (Prescriptions, Refills,
                            Formulary, Drug lookup) into one tabbed surface so
                            doctors don't have to disambiguate between four
                            Pill-icon items. The shell handles permission-aware
                            tab visibility + the refill counter badge. */}
                        <Route path="pharmacy" element={<PharmacyPage />}>
                            <Route path="prescriptions" element={<PrescriptionsListPage />} />
                            <Route path="refills" element={<RefillRequestsPage />} />
                            <Route path="formulary" element={<MedicationsPage />} />
                            <Route path="lookup" element={<DrugsPage />} />
                        </Route>
                        {/* Legacy cross-patient paths — redirect to the new
                            Pharmacy tabs so bookmarks, role-home quick-links,
                            and any prior emailed links keep working. */}
                        <Route path="prescriptions" element={<Navigate to="/pharmacy/prescriptions" replace />} />
                        <Route path="prescriptions/refills" element={<Navigate to="/pharmacy/refills" replace />} />
                        <Route path="medications" element={<Navigate to="/pharmacy/formulary" replace />} />
                        <Route path="drugs" element={<Navigate to="/pharmacy/lookup" replace />} />
                        {/* Deep Rx + medication sub-flows live at their
                            original paths — they're not cross-patient browse
                            destinations and don't belong inside the umbrella. */}
                        <Route path="prescriptions/import" element={<ImportPrescriptionsPage />} />
                        <Route path="prescriptions/new" element={<NewPrescriptionPage />} />
                        <Route path="prescriptions/session/:groupId" element={<PrescriptionSessionPage />} />
                        <Route path="prescriptions/:prescriptionId" element={<PrescriptionDetailsPage />} />
                        <Route path="patients/:patientId/prescriptions" element={<PatientPrescriptionsRedirect />} />
                        <Route path="patients/:patientId/prescriptions/new" element={<NewPrescriptionPage />} />
                        <Route path="patients/:patientId/prescriptions/:prescriptionId" element={<PrescriptionDetailsPage />} />
                        <Route path="medications/import" element={<ImportMedicationsPage />} />
                        <Route path="imaging-orders" element={<NavDietRoute to="/orders?type=imaging"><FeatureGuard moduleKey={ModuleKey.IMAGING_ORDERS}><ImagingOrdersPage /></FeatureGuard></NavDietRoute>} />
                        <Route path="imaging-orders/session/:groupId" element={<FeatureGuard moduleKey={ModuleKey.IMAGING_ORDERS}><ImagingOrderSessionPage /></FeatureGuard>} />
                        <Route path="imaging-orders/new" element={<FeatureGuard moduleKey={ModuleKey.IMAGING_ORDERS}><NewImagingOrderPage /></FeatureGuard>} />
                        <Route path="imaging-orders/:orderId" element={<FeatureGuard moduleKey={ModuleKey.IMAGING_ORDERS}><ImagingOrderDetailsPage /></FeatureGuard>} />
                        {/* Top-level scan-first document inbox. Self-gates on the AI_OCR_INBOX_ENABLED platform flag. */}
                        <Route path="scan" element={<ScanDocumentPage />} />
                        {/* <Route path="bed-management" element={<FeatureGuard moduleKey={ModuleKey.BED_MANAGEMENT}><BedManagementPage /></FeatureGuard>} /> */}
                        {/* <Route path="adt" element={<FeatureGuard moduleKey={ModuleKey.ADT}><ADTPage /></FeatureGuard>} /> */}
                        {/* <Route path="census" element={<FeatureGuard moduleKey={ModuleKey.CENSUS}><CensusPage /></FeatureGuard>} /> */}
                        <Route path="patients/:patientId/orders" element={<CPOEPage />} />
                        <Route path="patients/:patientId/orders/:orderId" element={<OrderDetailsPage />} />
                        <Route path="patients/:patientId/orders/:orderId/edit" element={<OrderDetailsPage />} />
                        <Route path="patients/:patientId/orders/medication/new" element={<MedicationOrderForm />} />
                        <Route path="patients/:patientId/orders/imaging/new" element={<ImagingOrderForm />} />
                        <Route path="patients/:patientId/orders/sets" element={<OrderSetsManager />} />
                        {/* The Library — one door with eight sections. The
                            section is the URL so the SubRail is linkable and
                            the back button walks it. */}
                        {/* The doctor's rail door. It pointed at /directory,
                            which had no top-level route at all — §13.3's "a
                            rail item that leads nowhere". The clinic-settings
                            copy at /clinic/directory stays where it is. */}
                        <Route path="directory" element={<ShellRoute design={<DoctorDirectoryPage />} legacy={<ExternalProvidersDirectoryPage />} />} />
                        <Route path="library" element={<FeatureGuard moduleKey={ModuleKey.TEMPLATES}><DoctorLibraryPage /></FeatureGuard>} />
                        <Route path="library/:section" element={<FeatureGuard moduleKey={ModuleKey.TEMPLATES}><DoctorLibraryPage /></FeatureGuard>} />
                        <Route path="templates" element={<FeatureGuard moduleKey={ModuleKey.TEMPLATES}><TemplateLibraryPage /></FeatureGuard>} />
                        <Route path="templates/new" element={<FeatureGuard moduleKey={ModuleKey.TEMPLATES}><TemplateBuilderPage /></FeatureGuard>} />
                        <Route path="templates/edit/:templateId" element={<FeatureGuard moduleKey={ModuleKey.TEMPLATES}><TemplateBuilderPage /></FeatureGuard>} />
                        <Route path="templates/use/:templateId" element={<FeatureGuard moduleKey={ModuleKey.TEMPLATES}><UseTemplatePage /></FeatureGuard>} />
                        <Route path="protocols" element={<FeatureGuard moduleKey={ModuleKey.TEMPLATES}><ProtocolLibraryPage /></FeatureGuard>} />
                        <Route path="protocols/new" element={<FeatureGuard moduleKey={ModuleKey.TEMPLATES}><ProtocolEditPage /></FeatureGuard>} />
                        <Route path="protocols/edit/:protocolId" element={<FeatureGuard moduleKey={ModuleKey.TEMPLATES}><ProtocolEditPage /></FeatureGuard>} />
                        {/* Lab + imaging order catalog — clinic-curated reference
                            list reused by the order-entry forms. Core EMR; the
                            backend enforces lab_order/imaging_order scopes. */}
                        <Route path="order-catalog" element={<OrderCatalogPage />} />
                        {/* One queue for everything you're waiting on. Labs,
                            imaging and referrals were three sidebar rows and
                            three pages with an identical shape; they're
                            segments now. The three old list URLs redirect —
                            every detail/new/session route is untouched. */}
                        <Route path="orders" element={<OrdersPage />} />
                        {/* One Clinic settings surface. The Administration group
                            put six admin rows in the sidebar above the fold,
                            competing with patient care for attention; they are all
                            set-up-once surfaces, so they live behind one row now.
                            Every /admin/* URL below still resolves — staff have
                            them bookmarked and notification rows carry them. */}
                        <Route path="clinic" element={<ClinicSettingsPage />}>
                          <Route index element={<Navigate to="team" replace />} />
                          <Route path="team" element={<MembersPage />} />
                          <Route path="roles" element={<RoleManagementPage />} />
                          <Route path="branches" element={<BranchesPanel />} />
                          <Route path="departments" element={<DepartmentsPanel />} />
                          <Route path="templates" element={<TemplateLibraryPage />} />
                          <Route path="protocols" element={<ProtocolLibraryPage />} />
                          <Route path="order-catalog" element={<OrderCatalogPage />} />
                          <Route path="knowledge-base" element={<KnowledgeBasePage />} />
                          {/* The design's Directory — canvas 6, the module of
                              record. The previous page stays at .../legacy
                              because adding and editing a provider still live
                              on it. */}
                          <Route path="directory" element={<ShellRoute design={<DoctorDirectoryPage />} legacy={<ExternalProvidersDirectoryPage />} />} />
                          <Route path="directory/legacy" element={<ExternalProvidersDirectoryPage />} />
                          <Route path="your-clinic" element={<YourClinicPanel />} />
                          <Route path="profile" element={<SystemSettingsPage />} />
                          <Route path="channels" element={<ChannelsPage />} />
                          <Route path="plan" element={<SubscriptionPage />} />
                          <Route path="audit-log" element={<AuditLogsPage />} />
                          <Route path="health" element={<SystemHealthPanel />} />
                        </Route>
                        <Route path="admin" element={<NavDietRoute to="/clinic"><AdminDashboard /></NavDietRoute>} />
                        <Route path="admin/users" element={<UserManagementPage />} />
                        <Route path="admin/members" element={<NavDietRoute to="/clinic/team"><MembersPage /></NavDietRoute>} />
                        <Route path="admin/domains" element={<NavDietRoute to="/clinic/profile#domains"><DomainsPage /></NavDietRoute>} />
                        <Route path="admin/roles" element={<NavDietRoute to="/clinic/roles"><RoleManagementPage /></NavDietRoute>} />
                        <Route path="admin/system-settings" element={<NavDietRoute to="/clinic/profile"><SystemSettingsPage /></NavDietRoute>} />
                        <Route path="admin/external-providers" element={<NavDietRoute to="/clinic/directory"><ExternalProvidersDirectoryPage /></NavDietRoute>} />
                        <Route path="admin/system-monitoring" element={<SystemMonitoringPage />} />
                        <Route path="admin/audit-logs" element={<NavDietRoute to="/clinic/audit-log"><AuditLogsPage /></NavDietRoute>} />
                        <Route path="admin/feature-toggles" element={<FeatureTogglePage />} />
                        {/* One setup surface. Bots, its hidden "Customize
                            messages" sub-page, and Patient portal (which sat
                            under Administration next to Audit Logs despite
                            being the clinic's free inbound channel) are now
                            tabs — see docs/INBOX_CONSOLIDATION_PLAN.md. */}
                        <Route path="channels" element={<ChannelsPage />} />
                        <Route path="admin/bots" element={<Navigate to="/channels" replace />} />
                        <Route path="admin/bots/customize" element={<Navigate to="/channels?tab=behaviour" replace />} />
                        <Route path="admin/patient-portal" element={<Navigate to="/channels" replace />} />
                        <Route path="admin/knowledge-base" element={<NavDietRoute to="/clinic/knowledge-base"><KnowledgeBasePage /></NavDietRoute>} />
                        {/* One inbox. Leads queue and Opt-outs became segments of it
                            (a lead is a thread with booking intent; an opt-out is a
                            flag on the thread's contact) — see
                            docs/INBOX_CONSOLIDATION_PLAN.md. The redirects are
                            load-bearing: staff_notifications rows already in the DB
                            carry the old actionUrls, so they must keep resolving. */}
                        <Route path="inbox" element={<InboxPage />} />
                        <Route path="communications" element={<Navigate to="/inbox" replace />} />
                        <Route path="admin/leads" element={<Navigate to="/inbox?segment=booking" replace />} />
                        <Route path="admin/opt-outs" element={<Navigate to="/inbox?segment=muted" replace />} />
                        {/* Clinic-owner view of the MAIE subscription. Distinct from
                            `/billing` (the clinic's *own* patient-billing surface). */}
                        <Route path="admin/subscription" element={<NavDietRoute to="/clinic/plan"><SubscriptionPage /></NavDietRoute>} />
                        {/* Hosted-checkout return target (Paymob redirection_url + Stripe success/cancel). */}
                        <Route path="admin/subscription/checkout" element={<CheckoutResultPage />} />
                        <Route path="billing" element={<FeatureGuard moduleKey={ModuleKey.BILLING}><BillingPage /></FeatureGuard>} />
                        <Route path="billing/invoices/new" element={<FeatureGuard moduleKey={ModuleKey.BILLING}><InvoiceFormPage /></FeatureGuard>} />
                        <Route path="billing/invoices/:id/edit" element={<FeatureGuard moduleKey={ModuleKey.BILLING}><InvoiceFormPage /></FeatureGuard>} />
                        <Route path="billing/:id" element={<FeatureGuard moduleKey={ModuleKey.BILLING}><BillDetailsPage /></FeatureGuard>} />
                        {/* <Route path="coding" element={<FeatureGuard moduleKey={ModuleKey.BILLING}><MedicalCodingPage /></FeatureGuard>} /> */}
                        {/* <Route path="reports" element={<ReportsPage />} /> */}
                        <Route path="notifications" element={<NotificationsPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="test" element={<TestPage />} />
                        <Route path="dicom-spike" element={<DicomSpikePage />} />
                        <Route path="*" element={<UnknownRouteHome />} />
            </Route>
        </Route>
    </Route>,
);